import React from 'react';
import {
  FaBriefcase,
  FaExternalLinkAlt,
  FaCalendarAlt,
} from 'react-icons/fa';
import { ProjectJobShift, ProjectMilestone } from '../../operations/services/PlantafelService';
import { CrmTask } from '../../sales/services/CrmTaskService';
import { getJobStatusColors } from './projectDetailUtils';
import styles from './ProjectDetailPage.module.css';

interface ProjectJobsTabProps {
  jobs: ProjectJobShift[];
  backlogTasks: CrmTask[];
  jobsLoading: boolean;
  milestones: ProjectMilestone[];
  projectName: string;
  onOpenJobInPlantafel: (job: ProjectJobShift) => void;
  onNavigateToPlantafel: () => void;
}

export function ProjectJobsTab({
  jobs,
  backlogTasks,
  jobsLoading,
  milestones,
  projectName,
  onOpenJobInPlantafel,
  onNavigateToPlantafel,
}: ProjectJobsTabProps): React.JSX.Element {
  return (
    <div>
      <div className={styles.tabHeader}>
        <h3 className={styles.tabTitle}>Jobs ({jobs.length + backlogTasks.length})</h3>
      </div>

      {jobsLoading ? (
        <div className={styles.loadingState}>Laden...</div>
      ) : jobs.length === 0 && backlogTasks.length === 0 ? (
        <div className={styles.emptyState}>
          <FaBriefcase size={40} color="#ccc" className={styles.emptyStateIcon} />
          <p className={styles.emptyText}>Keine Jobs in diesem Projekt</p>
        </div>
      ) : (
        <div className={styles.overflowX}>
          <table className={styles.jobsTable}>
            <thead>
              <tr className={styles.jobsTableHead}>
                <th className={styles.jobsTh}>Datum</th>
                <th className={styles.jobsTh}>Projekt</th>
                <th className={styles.jobsTh}>Milestone</th>
                <th className={styles.jobsTh}>Titel</th>
                <th className={styles.jobsTh}>Status</th>
                <th className={styles.jobsThCenter}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const jobDate = new Date(job.start_time);
                const _statusColors = getJobStatusColors(job.status);
                const jobMilestone = milestones.find((m) =>
                  m.jobs?.some((j) => j.id === job.crm_task_id),
                );
                return (
                  <tr key={`shift-${job.id}`} className={styles.jobRow}>
                    <td className={styles.jobsTd}>
                      <div className={styles.jobDatePrimary}>
                        {jobDate.toLocaleDateString('de-DE', {
                          weekday: 'short',
                          day: '2-digit',
                          month: '2-digit',
                        })}
                      </div>
                      <div className={styles.jobDateSecondary}>
                        {jobDate.toLocaleTimeString('de-DE', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </td>
                    <td className={`${styles.jobsTd} ${styles.jobProject}`}>
                      {projectName || '-'}
                    </td>
                    <td className={`${styles.jobsTd} ${styles.jobMilestone}`}>
                      {jobMilestone?.label || '-'}
                    </td>
                    <td className={`${styles.jobsTd} ${styles.jobTitle}`}>{job.title}</td>
                    <td className={styles.jobsTd}>
                      <span className={styles.statusBadge}>{job.status}</span>
                    </td>
                    <td className={styles.jobsTdCenter}>
                      <button
                        onClick={() => onOpenJobInPlantafel(job)}
                        className={styles.plantafelLink}
                        title="In Plantafel öffnen"
                      >
                        <FaExternalLinkAlt size={10} />
                        Plantafel
                      </button>
                    </td>
                  </tr>
                );
              })}

              {backlogTasks.map((task) => {
                const taskMilestoneId = task.payment_milestone ?? task.payment_milestone_id;
                let taskMilestone = taskMilestoneId
                  ? milestones.find((m) => m.id === taskMilestoneId)
                  : null;
                if (!taskMilestone) {
                  taskMilestone = milestones.find((m) => m.jobs?.some((j) => j.id === task.id));
                }
                const taskMilestoneLabel = task.payment_milestone_label || taskMilestone?.label;
                return (
                  <tr key={`task-${task.id}`} className={styles.jobRow}>
                    <td className={styles.jobsTd}>
                      <span className={styles.backlogBadge}>Backlog</span>
                    </td>
                    <td className={`${styles.jobsTd} ${styles.jobProject}`}>
                      {task.project_name || projectName || '-'}
                    </td>
                    <td className={`${styles.jobsTd} ${styles.jobMilestone}`}>
                      {taskMilestoneLabel || '-'}
                    </td>
                    <td className={`${styles.jobsTd} ${styles.jobTitle}`}>{task.title}</td>
                    <td className={styles.jobsTd}>
                      <span className={styles.statusBadge}>{task.status}</span>
                    </td>
                    <td className={styles.jobsTdCenter}>
                      <button
                        onClick={onNavigateToPlantafel}
                        className={styles.planLink}
                        title="In Plantafel planen"
                      >
                        <FaCalendarAlt size={10} />
                        Planen
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
